import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import PatientHeader from '../components/layout/PatientHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import StatusChip from '../components/shared/StatusChip';
import { Check, Upload, Shield, ChevronRight, AlertTriangle, Edit3, ArrowLeft, Wifi } from 'lucide-react';
import { toast } from 'sonner';

export default function CheckInPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [payerClass, setPayerClass] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');

  // Editable demographics
  const [editingDemo, setEditingDemo] = useState(false);
  const [demoForm, setDemoForm] = useState({ phone: '', address: '' });

  // Editable insurance
  const [editingInsurance, setEditingInsurance] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({ payer_name: '', member_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/checkin/${appointmentId}`);
      setAppointment(res.data.appointment);
      setPatient(res.data.patient);
      setPayerClass(res.data.appointment?.checkin?.payer_class || res.data.patient?.insurance?.payer_class || 'unknown');

      // Populate edit forms
      const p = res.data.patient;
      setDemoForm({ phone: p?.phone || '', address: p?.address || '' });
      setInsuranceForm({
        payer_name: p?.insurance?.payer_name || '',
        member_id: p?.insurance?.member_id || '',
      });

      const checkinStatus = res.data.appointment?.checkin?.status || 'not_started';
      if (checkinStatus === 'demographics_collected') setStep(1);
      else if (checkinStatus === 'insurance_uploaded') setStep(2);
      else if (['insurance_verified', 'needs_manual_review', 'ready_for_visit'].includes(checkinStatus)) setStep(3);
    } catch (err) {
      toast.error('Failed to load check-in data');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const confirmDemographics = async () => {
    try {
      // Send updates if fields were edited
      const updates = {};
      if (demoForm.phone !== patient?.phone) updates.phone = demoForm.phone;
      if (demoForm.address !== patient?.address) updates.address = demoForm.address;
      await api.post(`/checkin/${appointmentId}/demographics`, {
        confirmed: true,
        updates: Object.keys(updates).length > 0 ? updates : undefined,
      });
      toast.success('Demographics confirmed');
      setEditingDemo(false);
      setStep(1);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const uploadInsurance = async () => {
    try {
      await api.post(`/checkin/${appointmentId}/insurance`, {
        payer_name: insuranceForm.payer_name || patient?.insurance?.payer_name || '',
        member_id: insuranceForm.member_id || patient?.insurance?.member_id || '',
        payer_class: payerClass,
      });
      toast.success('Insurance information captured');
      setEditingInsurance(false);
      setStep(2);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const verifyInsurance = async (status) => {
    try {
      await api.post(`/checkin/${appointmentId}/verify`, {
        verification_status: status,
        verification_source: 'manual',
        verification_notes: verificationNotes,
        payer_class: payerClass,
        override_reason: status === 'could_not_verify' ? verificationNotes : undefined,
      });
      toast.success('Insurance verification recorded');
      setStep(3);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const markReady = async () => {
    try {
      await api.post(`/checkin/${appointmentId}/ready`);
      toast.success('Patient marked ready for provider');
      navigate('/schedule');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // Navigate to a previous step
  const goToStep = (targetStep) => {
    if (targetStep < step) {
      setStep(targetStep);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Loading...</p></div>;
  if (!appointment || !patient) return <div className="p-6 text-center text-muted-foreground">Appointment not found</div>;

  const steps = ['Demographics', 'Insurance', 'Verification', 'Ready'];
  const checkin = appointment.checkin || {};

  return (
    <div className="animate-fadeIn">
      <PatientHeader patient={patient} />

      <div className="p-4 sm:p-6 max-w-4xl">
        {/* Stepper — Clickable to go back */}
        <div className="flex items-center gap-2 mb-6" data-testid="check-in-stepper">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <button
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors
                  ${i === step ? 'bg-primary text-primary-foreground shadow-sm'
                    : i < step ? 'bg-primary/10 text-primary cursor-pointer hover:bg-primary/20'
                    : 'bg-muted text-muted-foreground cursor-default'}`}
                onClick={() => i < step && goToStep(i)}
                disabled={i >= step}
                data-testid={`step-${s.toLowerCase()}`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] border
                  ${i < step ? 'border-primary/30 bg-primary/20' : i === step ? 'border-primary-foreground/30' : 'border-muted-foreground/30'}`}>
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className="hidden sm:inline">{s}</span>
              </button>
              {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
            </React.Fragment>
          ))}
          <div className="flex-1" />
          <StatusChip status={checkin.status || 'not_started'} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Demographics */}
            <Card className={step > 0 && step !== 0 ? 'ring-1 ring-border/50' : ''} data-testid="demographics-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {step > 0 && <Check className="w-4 h-4 text-teal-500" />}
                    Demographics
                  </CardTitle>
                  {step > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => goToStep(0)} data-testid="edit-demographics-btn">
                      <Edit3 className="w-3 h-3" /> Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="font-medium">{patient.name?.first} {patient.name?.last}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">DOB</Label>
                    <p className="tabular-nums">{patient.dob}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Sex</Label>
                    <p>{patient.sex}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Phone</Label>
                    {step === 0 || editingDemo ? (
                      <Input className="h-8 text-sm mt-0.5" value={demoForm.phone}
                        onChange={e => setDemoForm(f => ({ ...f, phone: e.target.value }))}
                        data-testid="demo-phone-input" />
                    ) : (
                      <p>{patient.phone || 'Not on file'}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">Address</Label>
                    {step === 0 || editingDemo ? (
                      <Input className="h-8 text-sm mt-0.5" value={demoForm.address}
                        onChange={e => setDemoForm(f => ({ ...f, address: e.target.value }))}
                        data-testid="demo-address-input" />
                    ) : (
                      <p>{patient.address || 'Not on file'}</p>
                    )}
                  </div>
                </div>
                {step === 0 && (
                  <Button className="w-full mt-2" onClick={confirmDemographics} data-testid="check-in-demographics-confirm-toggle">
                    <Check className="w-4 h-4 mr-2" /> Confirm Demographics
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Insurance */}
            <Card className={step < 1 ? 'opacity-40 pointer-events-none' : ''} data-testid="insurance-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {step > 1 && <Check className="w-4 h-4 text-teal-500" />}
                    Insurance Information
                  </CardTitle>
                  {step > 1 && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => goToStep(1)} data-testid="edit-insurance-btn">
                      <Edit3 className="w-3 h-3" /> Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Payer</Label>
                    {step === 1 ? (
                      <Input className="h-8 text-sm mt-0.5" value={insuranceForm.payer_name}
                        onChange={e => setInsuranceForm(f => ({ ...f, payer_name: e.target.value }))}
                        placeholder="Insurance company"
                        data-testid="insurance-payer-input" />
                    ) : (
                      <p>{patient.insurance?.payer_name || 'Not on file'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Member ID</Label>
                    {step === 1 ? (
                      <Input className="h-8 text-sm mt-0.5" value={insuranceForm.member_id}
                        onChange={e => setInsuranceForm(f => ({ ...f, member_id: e.target.value }))}
                        placeholder="Member/Policy #"
                        data-testid="insurance-member-input" />
                    ) : (
                      <p>{patient.insurance?.member_id || 'Not on file'}</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Payer Classification</Label>
                  <Select value={payerClass} onValueChange={setPayerClass} disabled={step !== 1}>
                    <SelectTrigger className="h-8 text-xs mt-1" data-testid="check-in-payer-classification-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial" className="text-xs">Commercial</SelectItem>
                      <SelectItem value="medicare_rhc" className="text-xs">Medicare (RHC)</SelectItem>
                      <SelectItem value="medicaid_encounter" className="text-xs">Medicaid</SelectItem>
                      <SelectItem value="self_pay" className="text-xs">Self Pay</SelectItem>
                      <SelectItem value="unknown" className="text-xs">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {step === 1 && (
                  <Button className="w-full" onClick={uploadInsurance} data-testid="check-in-insurance-upload-input">
                    <Upload className="w-4 h-4 mr-2" /> Capture Insurance
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Verification */}
            <Card className={step < 2 ? 'opacity-40 pointer-events-none' : ''} data-testid="verification-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {step > 2 && <Check className="w-4 h-4 text-teal-500" />}
                    Insurance Verification
                  </CardTitle>
                  {step > 2 && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => goToStep(2)} data-testid="edit-verification-btn">
                      <Edit3 className="w-3 h-3" /> Edit
                    </Button>
                  )}
                </div>
                <CardDescription className="text-xs flex items-center gap-1">
                  <Wifi className="w-3 h-3" /> Manual verification — automated eligibility API planned for future release
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <Input value={verificationNotes} onChange={e => setVerificationNotes(e.target.value)}
                    placeholder="Verification notes..." className="mt-1" disabled={step !== 2} />
                </div>
                {step === 2 && (
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={() => verifyInsurance('active')}>
                      <Shield className="w-4 h-4 mr-1" /> Verify Active
                    </Button>
                    <Button variant="secondary" className="flex-1" onClick={() => verifyInsurance('active_needs_review')}>
                      Active (Needs Review)
                    </Button>
                    <Button variant="outline" onClick={() => verifyInsurance('could_not_verify')}>
                      <AlertTriangle className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Summary */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Check-In Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Demographics</span>
                  <Badge variant="outline" className="text-[10px]">{checkin.demographics_confirmed ? 'Confirmed' : 'Pending'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Insurance</span>
                  <Badge variant="outline" className="text-[10px]">{checkin.insurance_uploaded ? 'Captured' : 'Pending'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Verification</span>
                  <Badge variant="outline" className="text-[10px]">{checkin.verification_status || 'Pending'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payer Class</span>
                  <Badge variant="outline" className="text-[10px]">{checkin.payer_class || payerClass}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Back to Schedule */}
            <Button variant="outline" className="w-full h-9 text-xs gap-1" onClick={() => navigate('/schedule')} data-testid="back-to-schedule">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Schedule
            </Button>

            {step >= 3 && (
              <Button className="w-full h-12 text-sm font-semibold" onClick={markReady} data-testid="check-in-mark-ready-button">
                Mark Ready for Provider
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
